<template>
    <div>
        <div class="d-flex align-items-end justify-content-between gap-3">
            <b-skeleton-wrapper :loading="useTeamManagerData && loadingInitial" class="w-50">
                <template #loading>
                    <b-skeleton width="100%" height="1.5rem"></b-skeleton>
                </template>
                <h4 class="font-weight-bold text-primary-darker mb-0 text-nowrap"> {{ title }} </h4>
            </b-skeleton-wrapper>
            <div v-if="!loadingInitial && description" class="text-wrap text-muted font-size-70 mb-0">{{ description }}</div>
        </div>
        <hr class="bg-gray-400" />
        <b-alert :show="!!errorHandlingMessage" variant="danger" dismissible>{{ errorHandlingMessage }}</b-alert>
        <div 
            class=""
            v-if="!loadingInitial"
        >
            <flow-object-team-list-single-role
                v-for="roleOrGroup in roleGroupsForDisplay"
                :key="roleOrGroup.unique_key"
                :role-or-group="roleOrGroup"
                :loading-tus="loadingTus"
                :handle-remove-tu="handleRemoveTu"
                :start-add="startAdd"
                :is-collapsed="groupIsCollapsedStates[roleOrGroup.unique_key] || false"
                @toggle-collapse="handleToggleCollapseClick"
                class="mb-3"
            ></flow-object-team-list-single-role>
        </div>

        <b-modal
            v-model="addModalShowing"
            :title="`Add team member(s) to ${addingRoleGroup.name} group`"
            centered
            scrollable
            size="md"
            hide-footer
            @hidden="handleAddModalHidden"
        >
            <div v-if="loadingAddList" key="loadingAdd" class="d-flex align-items-center justify-content-center" style="min-height: 150px;">
                <b-spinner></b-spinner>
            </div>
            <div v-else-if="!loadingAddList && addList.length" key="addLoaded" class="h-auto">
                <div class="d-flex gap-3 h-100" style="">
                    <div class="w-50">
                        <h6 class="mb-2">Select Users:</h6>
                        <Listbox 
                            v-model="selectedTuIds" 
                            :options="addList" 
                            :multiple="true" 
                            :filter="true" 
                            dataKey="id"
                            optionLabel="full_name" 
                            optionValue="id"
                            optionDisabled="disabled"
                            listStyle="height: 300px; max-height: 300px;" 
                        ></Listbox>
                    </div>
                    <div class="w-50 d-flex flex-column">
                        <div class="mb-2 d-flex align-items-baseline justify-content-between">
                            <h6 class="mb-0">Users to Add:</h6>
                            <span class="small text-muted">{{ selectedTuIds.length ? selectedTuIds.length : 'No' }} users</span>
                        </div>
                        
                        <div class="flex-grow-1 p-3 bg-gray-100 border rounded">
                            <div class="d-flex flex-row flex-wrap align-items-start gap-2">
                                <tenant-user-badge
                                    v-for="tu in selectedAddTuObjects"
                                    :key="tu.id"
                                    :tu-data="tu"
                                    deselectable
                                    class="bg-white"
                                    @deselect="handleDeselectAddTu(tu)"
                                ></tenant-user-badge>
                            </div>
                        </div>
                    </div>
                </div>
                
                <!-- <b-form-checkbox-group
                    v-model="selectedTuIds"
                    :options="addList"
                    size="md"
                    value-field="id"
                    text-field="full_name"
                    stacked
                ></b-form-checkbox-group> -->
                <div class="mt-3 d-flex gap-3 justify-content-between">
                    <b-button 
                        size="md" 
                        variant="gray-dark" 
                        class="w-50"
                        :disabled="loadingAddRemove"
                        @click="handleAddCancel"
                    >
                        {{ loadingAddRemove ? 'Loading...' : 'Cancel' }}
                    </b-button>
                    <b-button 
                        size="md" 
                        variant="primary" 
                        class="w-50"
                        :disabled="loadingAddRemove || !selectedTuIds || !selectedTuIds.length"
                        @click="handleAddSave"
                    >
                        {{ loadingAddRemove ? 'Loading...' : 'Save' }}
                    </b-button>
                </div>
            </div>
            <div v-else-if="!loadingAddList && !addList.length" class="text-muted text-center min-height-100 d-flex align-items-center justify-content-center">
                No additional users available to add for this role.
            </div>
        </b-modal>

    </div>
</template>

<script>
import { errorHandling } from '@/js/custom-vue-mixins';
import { getFlowObjectApi } from '@/js/api-service';
import Listbox from 'primevue/listbox';
import cloneDeep from 'lodash/cloneDeep';
import { RoleOrGroupDisplay, makeDisplayFromTeamManagerRole, makeDisplayFromTeamManagerGroup } from './teamListUtils.js';
import FlowObjectTeamListSingleRole from './FlowObjectTeamListSingleRole.vue';

// TODO as of Nov 12 2023 - need to update this to work correctly with multiple roles per TU per obj
//  (when you actually remove that restriction in DB)
// and to make 'admin' less hard-coded

export default {
    name: 'FlowObjectTeamList',
    components: {
        Listbox,
        FlowObjectTeamListSingleRole,
    },
    mixins: [
        errorHandling,
    ],
    props: {
        flowObjectClass: {
            type: String,
            required: true,
        },
        flowSubtype: {
            type: String,
            default: null,
        },
        flowObjectId: {
            type: String,
            required: true,
        },
        flowContext: {
            type: Object,
            required: true,
        },
        teamManagerKey: {
            type: String,
            required: false,
        },
    },
    data: function() {
        return {
            errorMsg: 'Error',
            error: false,
            loadingExisting: false,
            objectTus: [],  // ObjectTenantUser instances (with .tenant_user nested inside)
            // ADD MODAL
            loadingAddList: false,
            addModalShowing: false,
            addList: [],
            addingRoleGroup: {},  // entire RoleOrGroupForDisplay - clone it here 
            selectedTuIds: [],
            // ROLES - likely just fetch once at the start
            objectRolesFromApi: [],  // the ObjectRole schema from API (not the one in team manager, and empty if we use team manager) - will have { id, name, description, slug } for each role available on this object
            loadingRoles: false,
            // TRIGGERS - these must match the constants in backend!
            addTriggerPrefix: 'add_otu_',  // you have a trigger for each role - ie: 'add_otu_admin'
            removeTriggerPrefix: 'remove_otu_',  // trigger for each role 
            // DOING THE THINGS
            loadingAddRemove: false,  // when actually doing the add/remove API calls
            // NEW Team Manager version
            initialTeamManagerLoaded: false,  // set true after first real load, or if we are NOT using it
            loadingTeamManager: false,
            teamManagerData: {},  // { title, description, layout, roles_and_groups }
            groupIsCollapsedStates: {},  // key is unique_key of group, value is bool for collapsed state, true means collapsed
        }
    },
    computed: {
        useTeamManagerData() {
            return !!this.teamManagerKey;
        },
        title() {
            const defaultTitle = 'Team';
            return this.useTeamManagerData ? this.teamManagerData.title || defaultTitle : defaultTitle;
        },
        description() {
            const defaultDesc = '';  // nothing...
            return this.useTeamManagerData ? this.teamManagerData.description || defaultDesc : defaultDesc;
        },
        roleGroupsForDisplay() {
            // watch out for when this re-renders... we want to make sure it happens when we refresh theh OTUs or the teamManagerData
            const forDisplay = [];
            if (this.useTeamManagerData) {
                // everything is already normalized from the backend for teamManagerData
                const topLevel = this.teamManagerData.roles_and_groups || [];
                topLevel.forEach(roleOrGroup => {
                    if (roleOrGroup.role_or_group === 'role') {
                        forDisplay.push(makeDisplayFromTeamManagerRole(roleOrGroup));
                    } else if (roleOrGroup.role_or_group === 'group') {
                        // the group handles its own children roles, passing through the normalized roles
                        forDisplay.push(makeDisplayFromTeamManagerGroup(roleOrGroup));
                    }
                });
            } else {
                // otherwise we use this.objectRolesFromApi, normalize each one
                this.objectRolesFromApi.forEach(r => {
                    forDisplay.push(this.makeRoleOrGroupDisplayFromObjectRole(r));
                });
            }
            return forDisplay;
            // return this.teamManagerKey ? this.roleGroupsFromTeamManager : this.roleGroups;
        },
        existingTuIds() {
            // tu IDs who are already members -- as of May 7 we may not need this anywhere since we now allow multiple roles per TU
            return this.objectTus.map(otu => otu.tenant_user.id);
        },
        adminTuCount() {  // ONLY USED FOR NON-TEAM MANAGER VERSION
            // just temporary, while we have hard-coded blocking of removing last 'admin' on front-end
            const adminRole = this.objectRolesFromApi?.find(r => r.slug === 'admin');  // yes, it is just .slug here... these are the raw ones from API
            const adminRoleId = adminRole ? adminRole.id : '';
            const adminTuCount = adminRoleId ? this.objectTus?.filter(otu => otu.role_id === adminRoleId).length : 0;
            return adminTuCount;
        },
        possibleTriggers() {
            // JUST FOR NOW (only for non-Team Manager version) - we hard-code a check to disable removing role if role slug is 'admin' and there is only one in that role -- so there is always at least one
            // for the stuff from TeamManager, this is already handled on backend
            const allPossible = this.flowContext?.triggers || [];
            if (this.useTeamManagerData) {
                // skipping the special stuff for team manager version -- the triggers are already filtered on backend
                // we may have more triggers available passed in from flowContext, but the local objects we make will have them properly disabled
                // this will be cleaner when we refactor this component to use the new team manager data only
                return allPossible;
            }
            const removeAdminTrigger = this.makeRemoveTriggerForObjectRoleSlug('admin');
            if (this.adminTuCount <= 1) {
                // if we have 0 or 1 admins, then we don't allow removing them 
                return allPossible.filter(trigName => trigName !== removeAdminTrigger);
            }
            return allPossible;
        },
        loadingTus() {
            return this.loadingAddRemove || this.loadingExisting || this.loadingTeamManager;
        },
        selectedAddTuObjects() {
            return this.addList.filter(tu => this.selectedTuIds.includes(tu.id));
        },
        flowObjectApi() {
            return getFlowObjectApi(this.flowObjectClass, this.flowSubtype);
        },
        loadingInitial() {
            return !this.initialTeamManagerLoaded || this.loadingRoles;
        },
    },
    mounted() {
        // we can call getTeamManagerData all the time -- it no-ops properly, and we need it to set a flag that it is done with its initial load
        this.getTeamManagerData();  // TODO - finish making a much simpler component using this structure - has all the things you need
        if (!this.useTeamManagerData) {
            this.getObjectRoles();
            this.getObjectTus();
        }
    },
    methods: {
        // use this.apiErrorHandler(error, extraMsg, localMsg) from mixin - localMsg is bool, if true puts message in this.errorHandlingMessage

        // RULES
        // - let external people only add other external people, and only if they are company admin
        // - let internal people add others if they are object admin, or tenant admin+

        // UTILS - just to keep things consistent when used from multiple places
        makeAddTriggerForObjectRoleSlug(roleSlug) {  // PHASE THIS OUT -- only for non-Team Manager version  (uses hard-coded prefix)
            return this.addTriggerPrefix + roleSlug;
        },
        makeRemoveTriggerForObjectRoleSlug(roleSlug) {  // PHASE THIS OUT -- only for non-Team Manager version (uses hard-coded prefix)
            return this.removeTriggerPrefix + roleSlug;
        },
        isTriggerAvailable(triggerName) {
            // return boolean - true if the trigger is available for current TU
            return this.possibleTriggers.includes(triggerName);
        },
        getTransitionObj(triggerName) {
            // pass in a trigger name (different for each role) and get the transition object (passed in flowContext)
            const transitions = this.flowContext?.transitions ?? [];
            return transitions.find(trans => (trans.trigger === triggerName));
            // let caller deal with not finding one....
        },
        makeRoleOrGroupDisplayFromObjectRole(apiObjectRole) {
            // normalize an ObjectRole from API schema to RoleOrGroupDisplay
            const addTrigger = this.makeAddTriggerForObjectRoleSlug(apiObjectRole.slug);
            const removeTrigger = this.makeRemoveTriggerForObjectRoleSlug(apiObjectRole.slug);
            return new RoleOrGroupDisplay({
                role_or_group: 'role',
                unique_key: apiObjectRole.id,
                name: apiObjectRole.name,
                description: apiObjectRole.description,
                role_slug: apiObjectRole.slug,
                add_trigger: addTrigger,
                remove_trigger: removeTrigger,
                can_add: this.isTriggerAvailable(addTrigger),
                can_remove: this.isTriggerAvailable(removeTrigger),
                tenant_users: this.objectTus.filter(otu => otu.role_id === apiObjectRole.id).map(otu => otu.tenant_user),  // remember you have ObjectTenantUser (the through model)
                empty_text: 'No Team Members',
            });
        },
        handleToggleCollapseClick(groupUniqueKey) {
            // toggle the collapse state for the given group key
            const currentState = this.groupIsCollapsedStates[groupUniqueKey] || false;
            console.log('handleToggleCollapseClick called for', groupUniqueKey, 'current state:', currentState);
            this.$set(this.groupIsCollapsedStates, groupUniqueKey, !currentState);
        },

        // LOAD EXISTING DATA
        // TODO - combine these into one call?  because we should really only get OTUs for the roles we can see (importing with TeamManagerConfig)
        async getTeamManagerData() {
            if (!this.teamManagerKey) {
                this.initialTeamManagerLoaded = true;  // so we know we are not waiting for it
                return;
            }
            this.loadingTeamManager = true;
            let resp;
            try {
                resp = await this.flowObjectApi.get_team_manager_data(this.flowObjectId, this.teamManagerKey);
                // resp.data will have {allowed_triggers: [], title: '', description: '', layout: '', roles_and_groups: []}
            } catch (error) {
                this.apiErrorHandler(error);
            }
            if (resp?.data) {
                this.teamManagerData = resp.data;  // could come back as empty object...
                const topLevelItems = this.teamManagerData.roles_and_groups || [];
                const groups = topLevelItems.filter(item => item.role_or_group === 'group');
                // initialize collapse states for groups if not already done
                // there is only one level of groups... groups cannot be nested for now at least
                groups.forEach(group => {
                    if (!(group.unique_key in this.groupIsCollapsedStates)) {
                        // only set this if not already set (means we already loaded it)
                        const isCollapsed = group.is_collapsible ? group.start_collapsed ?? false : false;
                        this.$set(this.groupIsCollapsedStates, group.unique_key, isCollapsed);
                    }
                });
            }
            this.loadingTeamManager = false;
            this.initialTeamManagerLoaded = true;  // so we know we are not waiting for it
        },
        async getObjectTus() {
            this.loadingExisting = true;
            let resp;
            try {
                resp = await this.flowObjectApi.get_object_tus(this.flowObjectId);
            } catch (error) {
                this.apiErrorHandler(error);
            }
            if (resp?.data) {
                this.objectTus = resp.data;  // not paginated... should be short 
            }
            this.loadingExisting = false;
        },
        async getObjectRoles() {
            this.loadingRoles = true;
            let resp;
            try {
                resp = await this.flowObjectApi.get_object_roles(this.flowObjectId);
            } catch (error) {
                this.apiErrorHandler(error);
            }
            if (resp?.data) {
                this.objectRolesFromApi = resp.data;
            }
            this.loadingRoles = false;
        },
        async refreshData() {
            if (this.useTeamManagerData) {
                await this.getTeamManagerData();
            } else {
                await this.getObjectTus();
                // should not need to reload this, at least we didn't before: this.getObjectRoles();
                // but we might need to since we build up the roleGroupsForDisplay from objectRolesFromApi + objectTus???
                // depends on what makes that trigger a re-render
            }
        },

        // START ADD
        startAdd(roleOrGroup) {
            this.addModalShowing = true;
            this.addingRoleGroup = cloneDeep(roleOrGroup);
            if (roleOrGroup.role_slug) {
                // groups don't have role_slug... so just being defensive
                this.getAddList(roleOrGroup.role_slug);
            }
        },
        async getAddList(roleSlug) {  // roleSlug can be left off to check all roles
            this.loadingAddList = true;
            let resp;
            try {
                resp = await this.flowObjectApi.get_possible_add_tus(this.flowObjectId, roleSlug);
            } catch (error) {
                this.apiErrorHandler(error);
            }
            if (resp?.data) {
                const addListAll = resp.data;
                this.addList = addListAll.map(tu => {
                    // no longer checking if existingTuIds includes the tu -- we allow multiple roles per TU now
                    //return {...tu, disabled: (this.existingTuIds.includes(tu.id) || tu.unassignable)};
                    return {...tu, disabled: tu.unassignable};
                });
            }
            this.loadingAddList = false;
        },
        resetAdd() {
            this.addModalShowing = false;  // probably already closed, but in case you call from other places
            this.addList = [];
            this.selectedTuIds = [];
            this.addingRoleGroup = {};
        },
        handleAddModalHidden() {
            this.resetAdd();
        },
        handleAddCancel() {
            this.addModalShowing = false;
        },
        handleDeselectAddTu(tuObj) {
            // when user clicks deselect on a TU badge in the add modal 
            // TU has not actually been added yet... just need to remove them from list of TUs to add
            this.selectedTuIds = this.selectedTuIds.filter(tuId => tuId !== tuObj.id);
        },

        // PERFORM ADD/REMOVE
        async handleAddSave() {
            // called from save button on modal 
            // do the add, close modal and reload list 
            await this.performAdd();
            // then close the modal and reset things
            this.handleAddCancel();
            this.refreshData();  // calls either getTeamManagerData or getObjectTus
            // this.getObjectTus();  // also need to refresh roles??? I don't think so...
            // this.getTeamManagerData();  // also refresh this
        },
        async performAdd() {
            // compare vs. existing to determine remove at same time???
            this.loadingAddRemove = true;
            const toAdd = [...this.selectedTuIds];
            const { role_slug, add_trigger } = this.addingRoleGroup;  // pull out the things you need
            if (!toAdd.length) {
                this.$toast('No new users to add');
                return;
            }
            const transObj = this.getTransitionObj(add_trigger);
            if (!transObj) {
                return;  // if we couldn't find the transition for some reason (would be undefined here)
            }
            const transPayload = {
                add_tu: toAdd,
                object_role: role_slug,
            };
            try {
                await this.flowContext.callTransition(transObj, transPayload);
                // currently no return from callTransition - but it does all the API calls, etc.
            } catch (error) {
                console.log(`Problem when trying to add TUs to role ${role_slug}`);  // callTransition probably dumped its own errors too
            }
            this.loadingAddRemove = false;
        },

        // START REMOVE
        async handleRemoveTu(tuObj, roleObj) {
            // TODO - must update this to work with multiple roles per TU per object! - we pass in roleObj to help with that
            // tuObj is passed in from the TU badge @deselect event 
            // show confirm modal? then execute
            // check that it can be done?  have to get the OTU from TU, get role from that, then make sure we have 'remove_otu_{role.slug}' trigger available
            console.log('tuObj, roleObj in handleRemoveTu:', tuObj, roleObj);
            const removeTrigger = roleObj.remove_trigger;  // should already be set properly
            if (!this.isTriggerAvailable(removeTrigger)) {
                this.$toast.warning('You are not allowed to do this.');
                return;
            }
            const confirmOk = await this.$bvModal.msgBoxConfirm(
                `Remove access for ${tuObj.full_name}?`, 
                {
                    title: "Remove Team Member",
                    centered: true,
                    size: 'sm',
                    okTitle: 'Yes, Remove',
                    okVariant: 'danger',
                    cancelVariant: 'gray-dark',
                    footerClass: 'border-0',
                });
            if (confirmOk) {
                console.log('User clicked OK to proceed with remove');
                await this.performRemoveTu(tuObj, roleObj);
                this.refreshData();  // calls either getTeamManagerData or getObjectTus
                // this.getObjectTus(); // refresh after we made the change 
                // this.getTeamManagerData(); // also refresh this
            } else {
                console.log('User clicked cancel to remove TU');
            }
            // if not confirmed, confirm modal is already closed on its own, nothing to reset here
        },
        async performRemoveTu(tuObj, roleObj) {
            console.log('Starting performRemoveTu for TU and role', tuObj, roleObj);
            this.loadingAddRemove = true;
            // const removeTrigger = this.makeRemoveTriggerForObjectRoleSlug(roleObj.slug);
            const removeTrigger = roleObj.remove_trigger;  // should already be set properly
            const roleSlug = roleObj.role_slug;
            const transObj = this.getTransitionObj(removeTrigger);
            if (!transObj) {
                console.log('No transObj');
                return;
            }
            const transPayload = {
                remove_tu: tuObj.id,
                // don't actually need role here... since each TU can only have one role in an object - actually now we do!
                object_role: roleSlug,
            };
            try {
                await this.flowContext.callTransition(transObj, transPayload);
                // currently no return from callTransition - but it does all the API calls, etc.
            } catch (error) {
                console.log(`Problem when trying to remove TUs`);  // callTransition probably dumped its own errors too
            }
            this.loadingAddRemove = false;
        },
    }
}
</script>

<style>

</style>